import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

import BaseDialog from '../../../BaseDialog'
import DatePicker from '../../../DatePicker'

import { useDataApi } from '../../../../context/DataApiContext'
import { useNotificationsProvider } from '../../../../context/NotificationsContext'
import validators from '../../../../utils/validators'

export default function CreateControlDialog({
  open,
  onClose,
  onCreated,
  selectedControl,
  selectedChassis,
  defaultDate = null,
}) {
  const { t } = useTranslation()

  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [nParc, setNParc] = useState('')
  const [controlDate, setControlDate] = useState(new Date())
  const [controllers, setControllers] = useState([])
  const [selectedControllerId, setSelectedControllerId] = useState('')
  const [loadingControllers, setLoadingControllers] = useState(false)

  const resetState = () => {
    // Clean form
    setLoading(false)
    setError('')
    setSelectedControllerId('')
    if (selectedControl) {
      setNParc(selectedControl.nParc)
      setControlDate(Date.parse(selectedControl.controlDate))
    } else if (selectedChassis) {
      setNParc(selectedChassis.nParc)
      setControlDate(defaultDate || new Date())
    } else {
      setNParc('')
      setControlDate(defaultDate || new Date())
    }
  }

  // Fetch controllers when dialog opens
  useEffect(() => {
    if (open && !selectedControl) {
      setLoadingControllers(true)
      dataProvider
        .get('/calendar/controllers', {})
        .then((response) => {
          setControllers(response.data.controllers || [])
          setLoadingControllers(false)
        })
        .catch(() => {
          setControllers([])
          setLoadingControllers(false)
        })
    }
  }, [open, selectedControl, dataProvider])

  const onCloseDialog = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      onClose()
    } else {
      resetState()
      onClose()
    }
  }

  const onConfirm = () => {
    const shouldValidateNParc = !selectedControl
    if (shouldValidateNParc) {
      if (nParc.length === 0) {
        setError('nParc_required')
        return
      }

      if (!validators.isValidNParc(nParc)) {
        setError('nParc_invalid')
        return
      }
    }

    if (controlDate === null) {
      setError('date_required')
      return
    }

    const controlDateISOString = controlDate.toISOString()
    const data = {
      controlDate: controlDateISOString,
    }
    if (!selectedControl) {
      data.nParc = nParc
      if (selectedControllerId) {
        data.controllerId = selectedControllerId
      }
    }
    setLoading(true)
    const remoteOp = selectedControl
      ? dataProvider.update('control', { data, id: selectedControl.controlId })
      : dataProvider.create('control', { data })

    remoteOp
      .then(() => {
        dataProvider.cache.reset()
        const titleKey = selectedControl
          ? 'edit-control-dialog.success'
          : 'create-control-dialog.success'
        showNotification('success', t(titleKey))
        onCreated()
        resetState()
        setLoading(false)
      })
      .catch((err) => {
        setLoading(false)
        if (
          err.response.data.validationErrors &&
          err.response.data.validationErrors.length > 0
        ) {
          showNotification(
            'error',
            err.response.data.validationErrors[0].errorMessage
          )
          setError(`${err.response.data.validationErrors[0].field}_invalid`)
        } else {
          showNotification('error', t('errors.api-error'))
        }
      })
  }

  useEffect(() => {
    if (selectedControl) {
      setNParc(selectedControl.nParc)
      setControlDate(Date.parse(selectedControl.controlDate))
    } else if (selectedChassis) {
      setNParc(selectedChassis.nParc)
    }
  }, [selectedControl, selectedChassis])

  // Set date when dialog opens with defaultDate
  useEffect(() => {
    if (open && defaultDate && !selectedControl) {
      const dateValue = new Date(defaultDate)
      // If the time is midnight (00:00), use current time instead
      // This happens when clicking on a day cell rather than a specific time slot
      if (dateValue.getHours() === 0 && dateValue.getMinutes() === 0) {
        const now = new Date()
        dateValue.setHours(now.getHours(), now.getMinutes(), 0, 0)
      }
      setControlDate(dateValue)
    }
  }, [open, defaultDate, selectedControl])

  let nParcHelperTest = null
  if (error === 'nParc_invalid') {
    nParcHelperTest = t('create-control-dialog.nParc-invalid-format')
  } else if (!selectedControl) {
    nParcHelperTest = t('common.required')
  }
  const titleKey = selectedControl
    ? 'edit-control-dialog.title'
    : 'create-control-dialog.title'
  return (
    <BaseDialog
      open={open}
      onClose={onCloseDialog}
      onConfirm={onConfirm}
      title={t(titleKey)}
      loading={loading}
    >
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <TextField
            variant="standard"
            required
            error={error === 'nParc_required' || error === 'nParc_invalid'}
            helperText={nParcHelperTest}
            id="nParc"
            label={t('common.nParc')}
            fullWidth
            value={nParc}
            onChange={(event) => setNParc(event.target.value)}
            disabled={!!selectedControl}
          />
        </Grid>
        <Grid item container spacing={2}>
          <Grid item xs={7}>
            <DatePicker
              id="startDate"
              value={controlDate}
              onChange={(newDate) => {
                if (newDate && controlDate) {
                  // Preserve the time when changing the date
                  newDate.setHours(
                    controlDate.getHours(),
                    controlDate.getMinutes(),
                    0,
                    0
                  )
                }
                setControlDate(newDate)
              }}
              label={t('common.controlDate')}
            />
          </Grid>
          <Grid item xs={5}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TimePicker
                label={t('common.time')}
                value={controlDate}
                onChange={(newTime) => {
                  if (newTime && controlDate) {
                    // Update only the time, keep the date
                    const updatedDate = new Date(controlDate)
                    updatedDate.setHours(
                      newTime.getHours(),
                      newTime.getMinutes(),
                      0,
                      0
                    )
                    setControlDate(updatedDate)
                  } else if (newTime) {
                    setControlDate(newTime)
                  }
                }}
                ampm={false}
                renderInput={(params) => (
                  <TextField {...params} variant="standard" fullWidth />
                )}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
        {!selectedControl && (
          <Grid item>
            <FormControl fullWidth variant="standard">
              <InputLabel id="controller-select-label">
                {t('create-control-dialog.controller')}
              </InputLabel>
              <Select
                labelId="controller-select-label"
                id="controller-select"
                value={selectedControllerId}
                onChange={(event) =>
                  setSelectedControllerId(event.target.value)
                }
                label={t('create-control-dialog.controller')}
                disabled={loadingControllers}
              >
                <MenuItem value="">
                  <em>{t('create-control-dialog.no-controller')}</em>
                </MenuItem>
                {controllers.map((controller) => (
                  <MenuItem key={controller.id} value={controller.id}>
                    {controller.firstName} {controller.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>
    </BaseDialog>
  )
}
